<template>
  <BittsLayout :is-page="true">
    <template #content>
      <ListsTable
        :checked-lists="checkedLists"
        :folder-id="folderUuid"
        :has-write-permissions="canCreateList"
        :trial-over="Boolean(isTrialOver)"
        @update:checked-lists="onUpdateCheckedLists"
        @edit-folder="onEditFolder"
        @delete-folder="onDeleteFolder"
      />
    </template>
  </BittsLayout>
  <BittsModal
    v-if="showDeleteModal"
    v-model="showDeleteModal"
    title="Delete Folder?"
    variant="confirm"
    :description="'Deleting this folder will remove it and the included reports for everyone in the organization. This action cannot be undone.'"
    primary-button-text="Delete"
    primary-button-type="danger"
    :action-loading="modalSaving.delete"
    @action="confirmDeleteFolder"
  />
  <BittsModal
    v-if="showEditModal"
    v-model="showEditModal"
    title="Edit Folder"
    :action-loading="modalSaving.edit"
    :width="400"
    :disabled="isFolderNameInvalid || !trimmedFolderName"
    @action="confirmEditFolder"
    @closed="cancelEditFolder"
  >
    <BittsInput
      v-model="newFolderName"
      name="folder-name"
      :status="isFolderNameInvalid ? 'danger' : 'default'"
      placeholder="New Folder Name"
      danger-text="Folder must have unique name."
    />
  </BittsModal>
  <router-view-wrapper />
</template>

<script setup lang="ts">
import { BittsInput, BittsLayout, BittsModal } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ListsTable from '@/components/reports/ListsTable.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import { captureException } from '@/errors';
import { useFlashesStore, useReportsStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';

const reportsStore = useReportsStore();
const { folders } = storeToRefs(reportsStore);
const flashesStore = useFlashesStore();

const router = useRouter();
const route = useRoute();
const folderUuid = computed(() => route.params.folder_id as string);

const title = computed(() => {
  const folder = reportsStore.getByFolderId(folderUuid.value);
  return folder?.name
    ? `${folder.name} - Folders - Crossbeam`
    : 'Folder - Crossbeam';
});
useHead({ title });

const { hasSalesOnlyRole } = storeToRefs(useRootStore());

const { hasScope } = useAuth();
const canCreateList = computed(
  () => hasScope('write:reports') && !hasSalesOnlyRole.value,
);
const { isTrialOver } = useConnectorTrialEndDate();

const showDeleteModal = ref(false);
const showEditModal = ref(false);
const modalSaving = ref({
  delete: false,
  edit: false,
});
const newFolderName = ref('');
const trimmedFolderName = computed(() => newFolderName.value.trim());
const isFolderNameInvalid = computed(() => {
  if (!trimmedFolderName.value) return false;
  return !!folders.value.find(
    (folder) => folder.name === trimmedFolderName.value,
  );
});

const checkedLists = ref<string[]>([]);
function onUpdateCheckedLists(lists: string[]) {
  checkedLists.value = lists;
}
function onEditFolder() {
  showEditModal.value = true;
}
function onDeleteFolder() {
  showDeleteModal.value = true;
}
async function confirmDeleteFolder() {
  try {
    if (!folderUuid.value) return;
    modalSaving.value.delete = true;
    await crossbeamApi.DELETE('/v0.1/report-folders/{uuid}', {
      params: {
        path: { uuid: folderUuid.value },
      },
    });
    flashesStore.addSuccessFlash({
      message: 'Report folder deleted',
    });
    await reportsStore.refreshReportsStore();
    await router.push({ name: 'lists' });
  } catch (err) {
    captureException(err);
    flashesStore.addErrorFlash({
      message: 'Something went wrong',
      description: 'This folder could not be deleted',
    });
  } finally {
    showDeleteModal.value = false;
    modalSaving.value.delete = false;
  }
}
async function confirmEditFolder() {
  try {
    if (!folderUuid.value) return;
    modalSaving.value.edit = true;
    await crossbeamApi.PATCH('/v0.1/report-folders', {
      body: {
        id: folderUuid.value,
        name: trimmedFolderName.value,
      },
    });
    newFolderName.value = '';
    await reportsStore.refreshReportsStore();
  } catch (_err) {
    flashesStore.addErrorFlash({
      message: 'Something went wrong',
      description: 'The folder could not be renamed',
    });
  } finally {
    showEditModal.value = false;
    modalSaving.value.edit = false;
  }
}

function cancelEditFolder() {
  newFolderName.value = '';
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
</style>
